import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { MapContext, OverlayView } from '@react-google-maps/api';
import { LAYER, LAYER_NZ } from '../../../../constants/map';
import { isNZ } from '../../../../constants/crux';
import GeoUtil from '../util/GeoUtil';
import MapOverlayImage from './MapOverlayImage';
import { UriBuilder } from 'js/helpers/UriBuilder';

const DEFAULT_HEIGHT = 766;
const DEFAULT_WIDTH = 1208;
const countryLayer = isNZ ? LAYER_NZ : LAYER;

export default class BoundingBoxLayer extends Component {
    static contextType = MapContext;

    static propTypes = {
        layerName: PropTypes.string,
        layerCategory: PropTypes.string,
        className: PropTypes.string,
        request: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        propertyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    };

    constructor(props) {
        super(props);
        this.state = {
            overlays: []
        };
        this.overlayIdCounter = 0;
        this.boundsChanged = createRef();
        this.zoomChanged = createRef();
        this.tilesLoaded = createRef();
    }

    componentDidMount() {
        this.addInitialOverlay();
        // Add an initial overlay when the component first mounts
        this.boundsChanged.current = this.context?.addListener('idle', this.addLayerOnTop);
        this.zoomChanged.current = this.context?.addListener('zoom_changed', this.addLayerInNewList);
    }

    // When zoom changes, clear overlay and add new one
    // When bounds changes, add new overlay on top of existing layers
    componentDidUpdate(prevProps) {
        if (!this.shouldCreateOverlay()) {
            return;
        }

        const layerTypeChanged = prevProps.layerName !== this.props.layerName;
        const requestParamChanged = isNZ
            ? prevProps.request?.param !== this.props.request?.param
            : prevProps.request !== this.props.request;

        if (layerTypeChanged || requestParamChanged) {
            this.addLayerInNewList();
        }
    }

    componentWillUnmount() {
        this.boundsChanged.current?.remove()
        this.zoomChanged.current?.remove()
    }

    getPropertyAsString = (obj, propName) => {
        const returnedPropName = Object.keys(obj).find(name => name === propName);
        if (returnedPropName) {
            return returnedPropName;
        }
        console.log(`Error: property name ${propName} is absent from object`);
        return '';
    }

    getMapDimension = () => {
        const map = this.context;
        const mapDiv = map?.getDiv();
        const height = mapDiv?.offsetHeight ?? DEFAULT_HEIGHT;
        const width = mapDiv?.offsetWidth ?? DEFAULT_WIDTH;
        return {
            height,
            width,
        }
    }

    constructUrl = (layerParam) => {
        const { layerName, propertyId } = this.props;
        const { height, width } = this.getMapDimension();
        const {
            north, east, south, west,
        } = this.context?.getBounds()?.toJSON();
        let mapLayerUrl;
        if (isNZ) {
            const { uri = '', param } = layerParam;
            const urlBuilder = new UriBuilder(uri)
                .addParam('bbox', `${GeoUtil.degrees2metersLon(west)},${GeoUtil.degrees2metersLat(south)},${GeoUtil.degrees2metersLon(east)},${GeoUtil.degrees2metersLat(north)}`)
                .addParam('bboxSR', '3857')
                .addParam('datumTransformations', '1565')
                .addParam('imageSR', '3857')
                .addParam('f', 'image')
                .addParam('size', `${width},${height}`)
                .addParam('transparent', 'true')
                .addParam('format', 'png32');

            if (layerName === 'pb') {
                // incorporate target property boundary
                urlBuilder.addParam('layers', 'show:0,1')
                    .addParam('layerDefs', `{0:'property_id IN (${propertyId})'}`);
            } else if (layerName === 'Target Property Boundary') {
                urlBuilder.addParam('layers', 'show:0')
                    .addParam('layerDefs', `{0:'property_id IN (${propertyId})'}`);
            } else if (!!param) {
                // decoding param from constants since they are encoded
                urlBuilder.addParam('layers', decodeURIComponent(param));
            }

            mapLayerUrl = urlBuilder.build();
        } else {
            const selectedBoundaryParam = ['Target Property Boundary', 'pboundary', 'parcelb'].includes(layerName)
                ? `&layers=show%3A0&layerDefs=%7B0:%27property_id%2BIN%2B(${propertyId})%27%7D` : '';
            mapLayerUrl = `${layerParam}?bbox=${GeoUtil.degrees2metersLon(west)}%2C${GeoUtil.degrees2metersLat(south)}%2C${GeoUtil.degrees2metersLon(east)}%2C${GeoUtil.degrees2metersLat(north)}&format=png32&transparent=true&size=${width}%2C${height}&dpi=96&f=image${selectedBoundaryParam}`;
        }

        return mapLayerUrl;
    }

    addInitialOverlay = () => {
        if(this.shouldCreateOverlay()) {
            this.addLayerOnTop();
            this.tilesLoaded.current?.remove();
        } else {
            this.tilesLoaded.current = this.context?.addListener('tilesloaded', this.addInitialOverlay);
        }
    }

    // Add a new overlay in existing list
    addLayerOnTop = () => {
        const newOverlay = this.createNewOverlay();
        this.setState(prevState => {
            // Only add if the new overlay is different from the last one
            const lastOverlay = prevState.overlays[prevState.overlays.length - 1];
            if (lastOverlay && lastOverlay.url === newOverlay.url) {
                return null; // No update needed
            }
            // Append the new overlay to the list
            return { overlays: [...prevState.overlays, newOverlay] };
        });
    }

    // Add a new overlay in an empty list
    addLayerInNewList = () => {
        this.setState({ overlays: [this.createNewOverlay()] });
    }

    shouldCreateOverlay = () => {
        const {
            layerCategory,
        } = this.props;

        if (!this.context?.getBounds()?.toJSON()) {
            return false;
        }

        return !(this.getPropertyAsString(countryLayer, 'propertyData') === layerCategory
            && this.context?.getZoom() < 15 && isNZ);
    }

    createNewOverlay = () => {
        const {
            layerCategory,
            className, request, layerName,
        } = this.props;

        let paneType;
        if (this.getPropertyAsString(countryLayer, 'boundary') === layerCategory ||
            this.getPropertyAsString(countryLayer, 'targetProperty') === layerCategory
        ) {
            paneType = OverlayView.MARKER_LAYER;
        } else if (this.getPropertyAsString(countryLayer, 'propertyData') === layerCategory) {
            paneType = OverlayView.OVERLAY_MOUSE_TARGET;
            if (layerName === 'pb') {
                paneType = OverlayView.OVERLAY_LAYER;
            }
        } else if (this.getPropertyAsString(countryLayer, 'baseMap') === layerCategory) {
            paneType = OverlayView.MAP_PANE;
        } else {
            paneType = OverlayView.OVERLAY_LAYER;
        }

        const url = this.constructUrl(request);
        return {
            id: `${layerName}${++this.overlayIdCounter}`,
            url,
            bounds: this.context?.getBounds()?.toJSON(),
            paneType,
            className,
            publicDomain: request?.publicDomain || '',
        }
    }

    //Remove the earliest layer when the number of layers reached limit
    removeFirstOverlayLayer = () => {
        const cacheLayerCount = 1;
        this.setState(prevState => {
            const newOverlays = [...prevState.overlays];
            if (newOverlays.length > cacheLayerCount) {
                return { overlays: newOverlays.slice(-cacheLayerCount) };
            } else {
                return null;
            }
        });
    }

    renderOverlays = () => {
        return this.state.overlays.map((overlay) => {
            const {
                id,
                url,
                paneType,
                className,
                publicDomain = '',
                bounds,
            } = overlay;

            const {
                north, east, south, west,
            } = bounds;

            const { height, width } = this.getMapDimension();
            this.removeFirstOverlayLayer();
            return (
                <OverlayView
                    key={id}
                    mapPaneName={paneType}
                    bounds={{ne: {lat: north, lng: east}, sw: {lat: south, lng: west}}}
                    getPixelPositionOffset={() => ({x: -width, y: -height})}
                >
                    <MapOverlayImage
                        publicDomain={publicDomain}
                        srcUrl={url}
                        className={className}
                    />
                </OverlayView>
            );
        });
    }

    render() {
        return (
            <div
                key={this.props.layerName}
                className="map-bounding-box-layer"
                data-testid="bounding-box-layer"
            >
                {this.renderOverlays()}
            </div>
        );
    }
}
